import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, CheckCircle2, Target, Database, GitBranch, TrendingUp,
  Award, Clock, Zap, BarChart2, Activity, ShieldCheck
} from 'lucide-react';

const SportsAnalytics = () => {
  const metrics = [
    { value: '65%', label: 'Prediction Accuracy', description: 'Soccer, NBA & NASCAR combined' },
    { value: '89.2%', label: 'Win Rate', description: 'Soccer Grade A Over/Under' },
    { value: '1 Month', label: 'Concept to Production', description: 'Deployment timeline' },
    { value: 'Positive ROI', label: 'From First Cycle', description: 'Commercial performance' },
  ];

  const services = [
    {
      icon: Target,
      title: 'Multi-Sport Prediction Platforms',
      description: 'Production-grade ML models trained on millions of historical records — calibrated for accuracy, not inflated for demo impressiveness.',
      outcomes: [
        'Soccer ensemble models (70K+ matches, 10 leagues)',
        'NBA XGBoost points and moneyline prediction models',
        'NASCAR track-type-specific model selection',
        'Custom sport modelling for new domains',
      ],
    },
    {
      icon: Database,
      title: 'Feature Engineering & Data Integration',
      description: 'The quality of prediction starts with the quality of features. We build the pipelines that turn raw sports data into predictive signals.',
      outcomes: [
        'Historical data pipelines: FootyStats, Sportradar, SportsDataIO',
        'Pre-match feature engineering at scale',
        'Real-time odds integration and movement tracking',
        'Multi-source data fusion with deduplication',
      ],
    },
    {
      icon: GitBranch,
      title: 'Automated Prediction Pipelines',
      description: 'Daily automated pipelines that fetch data, generate predictions, store results, validate output, and settle grades — without manual intervention.',
      outcomes: [
        'GitHub Actions automation: Fetch → Predict → Store → Validate',
        'Prediction storage and automated grading settlement',
        'Automated result collection and performance tracking',
        'Model performance monitoring and alerting',
      ],
    },
    {
      icon: BarChart2,
      title: 'ROI & Performance Analytics',
      description: 'Confidence-based grading systems and financial performance analytics that tell you what is working and what needs recalibration.',
      outcomes: [
        'Confidence-based grading systems (A+ through D)',
        'Sharpe ratio, variance, and risk analytics',
        'Break-even analysis and ROI tracking by grade',
        'Model interpretability and feature importance reporting',
      ],
    },
  ];

  const techStack = [
    { category: 'ML Models', items: ['XGBoost', 'LightGBM', 'RandomForest', 'Neural Networks'] },
    { category: 'Data Sources', items: ['FootyStats', 'Sportradar', 'SportsDataIO', 'The Odds API'] },
    { category: 'Infrastructure', items: ['Python', 'GitHub Actions', 'Azure PostgreSQL'] },
    { category: 'Analytics', items: ['Power BI', 'Pandas', 'Platt Scaling'] },
  ];

  const featuredResults = [
    { sport: 'Soccer', metric: 'Grade A O/U Win Rate', value: '89.2%', detail: '+0.28 units per bet' },
    { sport: 'NBA', metric: 'Grade A Moneyline ROI', value: '+18.5%', detail: '72.3% win rate' },
    { sport: 'NASCAR', metric: 'Top 10 Accuracy', value: '58.7%', detail: 'Track-specific models' },
    { sport: 'All Sports', metric: 'Time to Production', value: '1 Month', detail: 'Three sports shipped' },
  ];

  const useCases = [
    { name: 'Sports Technology Companies', desc: 'Prediction APIs and analytics engines as a product layer for sports tech platforms' },
    { name: 'Betting & Trading Platforms', desc: 'ML-powered edge identification for sports trading and market-making operations' },
    { name: 'Sports Organisations & Scouts', desc: 'Performance analytics and player evaluation models built on historical match data' },
    { name: 'Media & Content Platforms', desc: 'Predictive content, match previews, and analytical storytelling tools' },
  ];

  const whyChoose = [
    {
      icon: Zap,
      title: 'Production Not Research',
      description: 'We ship systems that run daily in production — not Jupyter notebooks. GitHub Actions automation, PostgreSQL output, live grading.',
    },
    {
      icon: TrendingUp,
      title: 'ROI-First Model Design',
      description: 'Models are calibrated for real-world betting value, not academic accuracy metrics. Grade A selections outperform across all sports.',
    },
    {
      icon: Award,
      title: 'Multi-Sport Architecture Experience',
      description: 'Soccer, NBA, and NASCAR shipped in one month. We know how to parallelise sport-specific modelling without duplicating effort.',
    },
    {
      icon: ShieldCheck,
      title: 'End-to-End Ownership',
      description: 'Data collection, feature engineering, model training, pipeline automation, and analytics — one team, no gaps.',
    },
  ];

  const faqs = [
    {
      q: 'What data do you need to start building?',
      a: 'We need historical match or event results with pre-match statistics available for both participants. The more history and the richer the feature set, the better the model performance. We can advise on the best data sources for your target sport.',
    },
    {
      q: 'How long does it take from data to a production pipeline?',
      a: 'Approximately 4–6 weeks per sport for a full pipeline including feature engineering, model training, validation, and automation. We shipped three sports in one month by running in parallel — so timeline depends on scope, not just complexity.',
    },
    {
      q: 'What accuracy should I expect?',
      a: 'Our Soccer Grade A Over/Under model achieves 89.2% win rate. NBA Grade A moneyline achieves 72.3% win rate with +18.5% ROI. These are calibrated figures from live production grading — not backtest inflation.',
    },
    {
      q: 'Can you expose predictions via API?',
      a: 'Yes. Our PostgreSQL output can be wrapped in REST API endpoints, allowing your platform or application to query predictions programmatically. We can build this as part of the engagement.',
    },
    {
      q: 'Can you build for sports not listed?',
      a: 'Yes. Cricket, tennis, rugby, and esports are all viable given appropriate historical data with pre-event statistics. Contact us with your sport and we will advise on data requirements and expected model performance.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>AI-Powered Sports Analytics & Prediction Systems | Agility</title>
        <meta
          name="description"
          content="Production-grade sports prediction ML systems for sports tech, betting platforms, and analytics teams. 89.2% win rate Soccer Grade A. Automated daily pipelines. Soccer, NBA, NASCAR."
        />
        <link rel="canonical" href="https://www.agilitytech.ai/sports-analytics" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AI-Powered Sports Analytics & Prediction Systems | Agility" />
        <meta property="og:description" content="Production-grade sports prediction ML systems. 89.2% win rate Soccer Grade A. Automated daily pipelines. Soccer, NBA, NASCAR." />
        <meta property="og:url" content="https://www.agilitytech.ai/sports-analytics" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-orange-900 via-black to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-orange-500 via-transparent to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full mb-8">
            <Activity className="w-4 h-4 text-orange-400" />
            <span className="text-orange-300 text-sm font-medium">Sports Analytics & Prediction AI</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            AI Sports Prediction<br />
            <span className="text-teal-400">That Ships to Production</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            Production-grade ML prediction systems for sports tech, betting platforms, and analytics teams.
            89.2% win rate on Soccer Grade A. Three sports deployed in one month.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
            >
              Schedule a Discovery Call
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/case-studies"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold border border-white/20 transition-all"
            >
              View Case Studies
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {metrics.map((m, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-bold text-teal-400 mb-1">{m.value}</div>
                <div className="text-white font-semibold mb-1">{m.label}</div>
                <div className="text-gray-400 text-sm">{m.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What We Build
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Four service areas covering the full sports analytics stack — from raw data to live production predictions.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="bg-gray-50 rounded-xl p-8 border border-gray-200 hover:border-teal-400 transition-all">
                  <Icon className="w-10 h-10 text-teal-600 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{s.title}</h3>
                  <p className="text-gray-600 mb-5 text-sm leading-relaxed">{s.description}</p>
                  <ul className="space-y-2">
                    {s.outcomes.map((o, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                        {o}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Results */}
      <section className="py-20 bg-gradient-to-br from-orange-700 to-teal-600 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Live Production Results</h2>
          <p className="text-orange-100 text-lg mb-10 max-w-2xl mx-auto">
            Calibrated figures from live production grading — not backtests.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {featuredResults.map((r, i) => (
              <div key={i} className="bg-white/10 rounded-xl p-5 border border-white/20">
                <div className="text-xs text-orange-200 uppercase tracking-wider mb-1">{r.sport}</div>
                <div className="text-2xl font-bold text-white mb-1">{r.value}</div>
                <div className="text-sm font-semibold text-teal-200 mb-1">{r.metric}</div>
                <div className="text-xs text-orange-100">{r.detail}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Technology Stack</h2>
            <p className="text-lg text-gray-600">Ensemble ML at the core, automated pipelines end-to-end.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {techStack.map((t, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">{t.category}</div>
                <ul className="space-y-2">
                  {t.items.map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-gray-800 text-sm font-medium">
                      <div className="w-1.5 h-1.5 bg-teal-500 rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Who We Build For</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {useCases.map((uc, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="text-teal-700 font-bold mb-2">{uc.name}</div>
                <p className="text-gray-600 text-sm leading-relaxed">{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why Sports Teams Choose <span className="text-teal-400">Agility</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChoose.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-white/5 rounded-xl p-8 border border-white/10 hover:border-teal-500/50 transition-all">
                  <Icon className="w-10 h-10 text-teal-400 mb-4" />
                  <h3 className="text-lg font-bold mb-3">{item.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-xl p-8 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{f.q}</h3>
                <p className="text-gray-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-orange-700 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Build Your Sports Prediction System?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Tell us your sport, data sources, and use case. We'll scope a plan in 48 hours.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-orange-700 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all text-lg"
          >
            Schedule a Discovery Call
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default SportsAnalytics;
